import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Container, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter.js";

//import utils
import { scrollToPosition } from "../../utils/utility.js";
import { formatPersianDate } from "../../utils/date.js";

//import constants
import { IS_MOBILE } from "../../constants/breakpoints.js";

//import general component
import MobileHeader from "../../components/common/mobile-header/mobile-header.js";
import BaseTitle from "../../components/common/base-title/base-title.jsx";
import { BaseButton } from "../../components/common/base-button/base-button.jsx";
import OldPassengersModal from "../../components/view/passengers-forms/old-passengers-modal.jsx";
import UserInformationModal from "../../components/common/user-information-login/user-information-modal.jsx";
import TabPanel from "../../components/common/base-tab/base-tabs-panel.jsx";
import BaseTabPanel from "../../components/common/base-tab/base-tab-panel.jsx";

//import view components
import IntHotelNestedForm from "../../components/view/passengers-forms/int-hotel-nested-form.jsx";
import HotelRegulationsTable from "../../components/view/passengers-forms/hotel-regulations-table.jsx";
import RulesLink from "../rules/rules-link.jsx";
import SelectedItem from "../../components/common/selected-item/selected-item.jsx";

//import images and icons
import Icon from "../../components/common/base-icon/icon.jsx";
import { InfoCircle } from "iconsax-react";

//import styles
import "./passengers.scss";

export default function IntHotelPassengers() {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const accessToken = localStorage.getItem("accessToken");
    // const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isUserInformationOpen, setIsUserInformationOpen] = useState(false);

    //sessionStorage variables
    const userInformation = JSON.parse(localStorage.getItem("userInformation"));
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const selectedFlight = JSON.parse(sessionStorage.getItem("selectedFlight"));
    const selectedHotel = JSON.parse(sessionStorage.getItem("selectedHotel"));
    const selectedIntRooms = JSON.parse(sessionStorage.getItem("selectedIntRooms"));
    const intHotelPassengers = JSON.parse(sessionStorage.getItem("intHotelPassengers"));
    const { nationality } = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));

    useEffect(() => {
        scrollToPosition(0, 0);
    }, []);

    const createPassenger = () => ({
        english_first_name: "",
        english_last_name: "",
        type: "",
        age: "",
        title: "",
        gender: "",
        nationality: "",
    });

    const createRoomPassengers = (room, index) => {
        const totalTravelers = room?.travelers?.adult_count[index + 1] + room?.travelers?.child_count[index + 1];
        return Array.from({ length: totalTravelers }, createPassenger);
    };

    const createRooms = selectedRooms => {
        return Object.values(selectedRooms?.rooms).map((room, index) => ({
            name: "passengers",
            passengers: createRoomPassengers(room, index),
        }));
    };

    const defaultValues = {
        rooms: createRooms(selectedIntRooms),
    };

    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        getValues,
        resetField,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues: intHotelPassengers || defaultValues,
        mode: "all",
        delayError: 500,
    });

    const { fields: rooms } = useFieldArray({
        control,
        name: "rooms",
    });

    const handleRegistration = data => {
        if (IS_MOBILE) {
            // Create array of required fields
            const requiredFields = ["english_first_name", "english_last_name", "gender"];

            // Validate each passenger form
            const isValid = data.rooms.every((room, roomIndex) => {
                return room.passengers.every((formData, passengerIndex) => {
                    const allFields = [...requiredFields];
                    if (nationality?.id === 1) {
                        allFields.push("national_code");
                    }

                    const isFormValid = allFields.every(field => formData[field]);

                    if (!isFormValid) {
                        setTabValue(roomIndex);
                        toast.error(t("Toast Messages.Complete Forms for Each Room"));
                        return false;
                    }
                    return true;
                });
            });

            if (!isValid) return;
        }

        if (accessToken) {
            if (
                !!userInformation?.first_name &&
                !!userInformation?.last_name &&
                !!userInformation?.phone_number &&
                !!userInformation?.national_code
            ) {
                sessionStorage.setItem("intHotelPassengers", JSON.stringify(data));
                navigate(`/${locale}/confirm-information`);
            } else {
                setSearchParams({ action: "userInformation" });
            }
        } else {
            setSearchParams({ action: "login" });
        }
    };

    const handleError = error => {
        console.log(error);
    };

    //handel the selection of passenger from previous passenger
    const [isPassengersModalOpen, setIsPassengersModalOpen] = useState(false);
    const handleSelectPassenger = (passenger, index, roomIndex) => {
        clearErrors("rooms");
        setValue(
            `rooms.${roomIndex}.passengers.${index}`,
            {
                english_first_name: passenger?.english_first_name,
                english_last_name: passenger?.english_last_name,
                gender: passenger?.gender,
            },
            { shouldValidate: true },
        );

        nationality?.id === 1 &&
            setValue(`rooms.${roomIndex}.passengers.${index}.national_code`, passenger.national_code, {
                shouldValidate: true,
            });
    };

    //handle modals
    useEffect(() => {
        if (searchParams.get("action") === "userInformation") setIsUserInformationOpen(true);
        else setIsUserInformationOpen(false);

        if (searchParams.get("action") === "old-passengers") setIsPassengersModalOpen(true);
        else setIsPassengersModalOpen(false);

        // if (searchParams.get("action") === "login") setIsLoginOpen(true);
        // else setIsLoginOpen(false);
    }, [searchParams]);

    //handle form tabs on mobile
    const [tabValue, setTabValue] = useState(0);
    const handleTabChange = (event, newValue) => {
        if (!errors.rooms?.[tabValue]) {
            scrollToPosition(0, 0);
            setTabValue(newValue);
        }
    };

    //handle navigate back on Mobile
    const navigateBack = () => {
        if (isItinerary) {
            if (selectedFlight) {
                navigate(`/${locale}/flight-passengers`, { replace: true });
            } else {
                navigate(`/${locale}/itinerary/hotel`, { replace: true });
            }
        } else navigate(`/${locale}/hotel`, { replace: true });
    };

    //selected item data used for display and navigation
    const data = {
        title: selectedHotel.name,
        star: selectedHotel.star,
        name: [Object.keys(selectedIntRooms?.rooms)?.length + " " + t("room")],
        image: selectedIntRooms?.images?.[0]?.image,
        date: `${formatPersianDate(hotelInformation.checkIn, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: "long",
        })} ${t("to-2")} ${formatPersianDate(hotelInformation.checkOut, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: "long",
        })}`,
        totalPrice: selectedIntRooms.fare?.total,
        type: "intHotel",
        details: Object.values(selectedIntRooms?.rooms),
        route: "hotel",
    };

    return (
        <Container maxWidth="xl">
            <Grid className="passengers" container item xs={12} rowGap={3} mb={rooms?.length > 1 ? 30 : 26}>
                <MobileHeader title={t("roomsInformation")} justifyContent="center" handleClick={navigateBack} />

                <Grid container item xs={12} rowGap={3} columnGap={1} mt={ IS_MOBILE ? 7 : 0}>
                    <SelectedItem data={data} />
                </Grid>

                <Grid component="form" container item xs={12} rowGap={3} mt={2} onSubmit={handleSubmit(handleRegistration, handleError)}>
                    {IS_MOBILE ? (
                        <Grid className="passengers__forms" container item xs={12}>
                            {rooms.map((room, roomIndex) => {
                                return (
                                    <TabPanel key={room.id} value={tabValue} index={roomIndex}>
                                        <IntHotelNestedForm
                                            roomIndex={roomIndex}
                                            {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                            isPassengersModalOpen={isPassengersModalOpen}
                                        />
                                    </TabPanel>
                                );
                            })}

                            {rooms?.length > 1 ? (
                                <BaseTabPanel list={rooms} tabValue={tabValue} handleTabChange={handleTabChange} isError={errors.rooms?.[tabValue]} />
                            ) : null}
                        </Grid>
                    ) : (
                        <>
                            <HotelRegulationsTable />
                            <BaseTitle
                                title={
                                    <Grid container item xs={12} columnGap={1} justifyContent="space-between" alignItems="center">
                                        <Icon name="BedroomIcon" /> {t("roomsInformation")}
                                    </Grid>
                                }
                                dividerColor="1px solid var(--medium-gray-color)"
                            />
                            <Grid className="passengers__date-warning" component="span" container item xs={12} columnGap={1}>
                                <InfoCircle size="20" color="var(--mustard-color)" />
                                هشدار: ملیت انتخاب شده در زمان جستجوی هتل، برای تمامی مسافرین در نظر گرفته می شود، در صورت مغایرت عواقب ناشی از آن به
                                عهده مسافر می باشد.{" "}
                            </Grid>
                            <Grid className="passengers__forms" container item xs={12} rowGap={5}>
                                {rooms.map((room, roomIndex) => {
                                    return (
                                        <IntHotelNestedForm
                                            key={room.id}
                                            roomIndex={roomIndex}
                                            {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                            isPassengersModalOpen={isPassengersModalOpen}
                                        />
                                    );
                                })}
                            </Grid>
                        </>
                    )}
                    <Grid
                        className="passengers__submit-information"
                        container
                        item
                        xs={12}
                        rowGap={2}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <RulesLink />
                        <Grid container item xs={12} md={2} justifyContent="flex-end">
                            <BaseButton text={t("submitInformation")} color="primary" size="large" radius="radius-10" type="submit" />
                        </Grid>
                    </Grid>
                </Grid>

                {/* <LoginModal open={isLoginOpen} setOpen={setIsLoginOpen} /> */}
                <UserInformationModal open={isUserInformationOpen} setOpen={setIsUserInformationOpen} />
                <OldPassengersModal open={isPassengersModalOpen} setOpen={setIsPassengersModalOpen} selectedOldPassenger={handleSelectPassenger} />
            </Grid>
        </Container>
    );
}
